import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import db from '../services/db';
import { TimerMode } from '../components/molecules/TimerCard';

// 计时器数据接口
export interface TimerData {
  id: string;
  name: string;
  hours: number;
  minutes: number;
  seconds: number;
  mode: TimerMode;
  showMilliseconds: boolean;
  createdAt: number;
}

// 计时器状态接口
interface TimersState {
  items: TimerData[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// 初始状态
const initialState: TimersState = {
  items: [],
  status: 'idle',
  error: null,
};

// 异步操作：获取所有计时器
export const fetchTimers = createAsyncThunk(
  'timers/fetchAll',
  async () => {
    return await db.getTimers();
  }
);

// 异步操作：添加计时器
export const addTimer = createAsyncThunk(
  'timers/add',
  async (timer: Omit<TimerData, 'id' | 'createdAt'>) => {
    return await db.addTimer(timer);
  }
);

// 异步操作：更新计时器
export const updateTimer = createAsyncThunk(
  'timers/update',
  async ({ id, changes }: { id: string; changes: Partial<Omit<TimerData, 'id' | 'createdAt'>> }) => {
    return await db.updateTimer(id, changes);
  }
);

// 异步操作：删除计时器
export const deleteTimer = createAsyncThunk(
  'timers/delete',
  async (id: string) => {
    await db.deleteTimer(id);
    return id;
  }
);

// 创建计时器Slice
const timersSlice = createSlice({
  name: 'timers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // 获取所有计时器
      .addCase(fetchTimers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTimers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchTimers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '获取计时器失败';
      })
      
      // 添加计时器
      .addCase(addTimer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addTimer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
      })
      .addCase(addTimer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '添加计时器失败';
      })
      
      // 更新计时器
      .addCase(updateTimer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateTimer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.items.findIndex(timer => timer.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateTimer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '更新计时器失败';
      })
      
      // 删除计时器
      .addCase(deleteTimer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteTimer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(timer => timer.id !== action.payload);
      })
      .addCase(deleteTimer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '删除计时器失败';
      });
  },
});

export default timersSlice.reducer;